import { useSelector } from 'react-redux';

import { dateUtils } from '@makemydeal/dr-common-utils';
import * as multiScenarioSelectors from '@makemydeal/dr-dash-store/selectors/multiScenarioSelectors';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { DARWIN_APPLICATION_NAME } from '@makemydeal/dr-dash-store/utils/darwinEventUtils';
import { DocumentTextIcon } from '@interstate/components/Icons/DocumentTextIcon';
import { CheckmarkSolidIcon } from '@interstate/components/Icons/CheckmarkSolidIcon';
import { ExclamationTriangleIcon } from '@interstate/components/Icons/ExclamationTriangleIcon';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const useGetDealStatusInfo = () => {
    const showUnsavedChanges = useSelector(unsavedChangesSelectors.currentOfferHasUnsavedChanges);
    const lastSuccessfulSaveDateString = useSelector(offerInfoSelectors.getLastSuccessfulSaveDateString);
    const activeComparison = useSelector(multiScenarioSelectors.getActiveComparison);
    const moreRecentVersionAvailable = useSelector(multiScenarioSelectors.isMoreRecentVersionAvailable);
    const enableStaleDraftVersionDiscard = useSelector(featureToggleSelectors.enableStaleDraftVersionDiscard);
    const isLogicalDraftRetentionEnabled = useSelector(featureToggleSelectors.isLogicalDraftRetentionEnabled);
    const applicationName = useSelector(offerSelectors.getApplicationName);
    const isNonDarwinUpdate = isLogicalDraftRetentionEnabled && applicationName !== DARWIN_APPLICATION_NAME;

    const lastSavedDate = lastSuccessfulSaveDateString && new Date(lastSuccessfulSaveDateString);
    const createdOnFormattedDate = lastSavedDate && dateUtils.formatAsMMDDYYYY(lastSavedDate);
    const createdOnFormattedTime = lastSavedDate && dateUtils.formatAsTime(lastSavedDate);
    const isPublished = activeComparison?.isPublished ?? true;

    // Case 1: Unsaved changes banner
    if (showUnsavedChanges) {
        const icon = <ExclamationTriangleIcon color="#ffc20e" />;

        const text = (
            <Typography variant="body-sm" tag="span">
                Unsaved Changes
            </Typography>
        );

        return { isVisible: true, icon, text, isWarning: true };
    }

    // Case 2: no last-saved information or stale draft version -> show nothing
    // When logical draft retention is enabled and the update is non-Darwin, keep the draft badge visible
    if (!lastSavedDate || (enableStaleDraftVersionDiscard && moreRecentVersionAvailable && !isNonDarwinUpdate)) {
        return { isVisible: false, icon: null, text: null, isWarning: false };
    }

    // Case 3: saved, show status + timestamp
    const icon = !isPublished ? <DocumentTextIcon color="#005BA8" /> : <CheckmarkSolidIcon color="#3c7b29" />;

    const text = (
        <Typography variant="body-sm" tag="span">
            {isPublished ? (
                'Published'
            ) : (
                <>
                    <Typography variant="strong-sm" sx={{ fontWeight: 700 }}>
                        Draft Deal
                    </Typography>{' '}
                    - Saved
                </>
            )}{' '}
            {createdOnFormattedDate} at {createdOnFormattedTime}
        </Typography>
    );

    return { isVisible: true, icon, text, isWarning: false };
};
